import type { OrderDetails } from '@/entities/order/model'
import { notifications } from '@mantine/notifications'
import { useCallback } from 'react'
import { useStateConfigQuery, useZebraBarcodeEnabled } from '@/entities/state/model'
import { isOrderAccountedForGroupScan, isZebraScanProgressStatus } from './order.zebraScan'
import { isGroupGatedAction } from './useGroupTransitionGuard'
import { useOrderGroupMembers } from './useOrderGroupMembers'

/** BD-8644: shown instead of letting the backend reject an unscanned single order. */
export function notifyOrderScanIncomplete(): void {
  notifications.show({
    color: 'red',
    title: 'Order not fully scanned',
    message: 'Every crate in this order must be scanned or marked missing before it can move on.',
    autoClose: 5000,
  })
}

/**
 * BD-8644: a single (non-grouped) order may not move on while its crates are outstanding.
 *
 * Same condition as the BD-8186 group gate, applied to the order on its own: scanning
 * applies (feature on, order in the scan window) and a crate is neither scanned nor its
 * type marked missing. An order with no expected crates is never blocked, so BD-8590
 * "Proceed without scanning" still completes. Grouped orders are left to
 * `useGroupTransitionGuard`, which keeps the "Group not fully scanned" wording.
 */
export function useOrderTransitionGuard(order: OrderDetails | undefined) {
  const zebraBarcodeEnabled = useZebraBarcodeEnabled()
  const { data: stateConfig } = useStateConfigQuery()
  const groupMemberOrderIds = useOrderGroupMembers(order?.orderId ?? '')

  const isBlockedByOrderScan = Boolean(order)
    && groupMemberOrderIds.length <= 1
    && zebraBarcodeEnabled
    && isZebraScanProgressStatus(order?.status, stateConfig?.states)
    && !isOrderAccountedForGroupScan(order as OrderDetails)

  const isOrderTransitionBlocked = useCallback(
    (action: string) => isBlockedByOrderScan && isGroupGatedAction(action),
    [isBlockedByOrderScan],
  )

  return { isOrderTransitionBlocked }
}
